import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck,
    BadgeCheck,
    Factory,
    Zap,
    Headphones,
    Award,
    Flame,
    Droplets,
    Siren,
    ShieldAlert,
    Wrench,
    Volume2,
    Settings,
    ClipboardCheck,
    Workflow,
    ChevronRight,
    Quote,
    ArrowUpRight,
    Phone,
    MapPin,
    CheckCircle2,
    Sparkles,
} from "lucide-react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "../components/ui/carousel";
import { Counter } from "../components/Counter";
import { LeadForm } from "../components/LeadForm";
import BlogCard from "../components/BlogCard";
import { fetchPublicBlogs } from "../lib/publicBlogs";
import {
    COMPANY,
    STATS,
    TRUST_BADGES,
    SERVICES,
    WHY_US,
    PRODUCTS,
    INDUSTRIES,
    TESTIMONIALS,
    CLIENT_LOGOS,
    HERO_IMAGE,
    ABOUT_IMAGE,
} from "../data/site";

const ICONS = {
    ShieldCheck,
    BadgeCheck,
    Factory,
    Zap,
    Headphones,
    Award,
    Flame,
    Droplets,
    Siren,
    ShieldAlert,
    Wrench,
    Volume2,
    Settings,
    ClipboardCheck,
    Workflow,
};

const Icon = ({ name, className = "w-5 h-5" }) => {
    const C = ICONS[name] || Flame;
    return <C className={className} />;
};

const Home = () => {
    return (
        <>
            <Hero />
            <TrustMarquee />
            <ServicesGrid />
            <AboutStats />
            <WhyChooseUs />
            <ProductsCarousel />
            <BlogInsights />
            <IndustriesGrid />
            <Testimonials />
            <ContactSection />
        </>
    );
};

const Hero = () => {
    return (
        <section
            className="relative pt-12 md:pt-16 pb-20 md:pb-28 overflow-hidden bg-white"
            data-testid="hero-section"
        >
            {/* Background ambient layers */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="red-glow -left-60 -top-40" />
                <div
                    className="absolute -right-40 -top-20 w-[600px] h-[600px] opacity-40"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(14,77,164,0.18) 0%, transparent 60%)",
                    }}
                />
                <div
                    className="absolute top-0 right-0 w-2/3 h-full opacity-50"
                    style={{
                        background:
                            "radial-gradient(ellipse at top right, #e8f1fc 0%, transparent 55%)",
                    }}
                />
                {/* Dot grid */}
                <div
                    className="absolute inset-0 opacity-[0.05]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle, #E53935 1px, transparent 1px)",
                        backgroundSize: "26px 26px",
                    }}
                />
                {/* Diagonal accent lines */}
                <svg
                    className="absolute -bottom-20 -right-20 w-96 h-96 opacity-10"
                    viewBox="0 0 200 200"
                    fill="none"
                >
                    <path d="M0 200 L200 0" stroke="#E53935" strokeWidth="1" />
                    <path d="M20 200 L200 20" stroke="#0E4DA4" strokeWidth="1" />
                    <path d="M40 200 L200 40" stroke="#E53935" strokeWidth="1" />
                    <path d="M60 200 L200 60" stroke="#0E4DA4" strokeWidth="1" />
                </svg>
            </div>

            <div className="container-vil relative z-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    {/* Left content */}
                    <div className="lg:col-span-7">
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 mb-7 bg-white border border-[#E53935]/25 shadow-sm"
                            style={{ borderRadius: 2 }}
                            data-testid="hero-badge"
                        >
                            <span className="relative flex w-2 h-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-[#E53935] opacity-60 animate-ping" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E53935]" />
                            </span>
                            <span className="eyebrow !text-[10px]">
                                Licensed · Government Approved
                            </span>
                        </div>

                        <h1
                            className="font-display font-black leading-[0.95] mb-7 text-slate-900"
                            style={{ fontSize: "clamp(2.4rem, 5.6vw, 5.2rem)" }}
                        >
                            Complete{" "}
                            <span className="text-[#E53935]">Fire Protection</span>{" "}
                            <span className="text-slate-300 font-light">/</span>{" "}
                            <span className="relative inline-block">
                                <span className="text-[#0E4DA4]">Life Safety</span>
                                <svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    height="10"
                                    viewBox="0 0 400 10"
                                    preserveAspectRatio="none"
                                >
                                    <defs>
                                        <linearGradient id="underlineGrad" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor="#E53935" />
                                            <stop offset="100%" stopColor="#0E4DA4" />
                                        </linearGradient>
                                    </defs>
                                    <path
                                        d="M0 5 Q100 0 200 5 T400 5"
                                        stroke="url(#underlineGrad)"
                                        strokeWidth="3"
                                        fill="none"
                                    />
                                </svg>
                            </span>{" "}
                            Solutions
                        </h1>

                        <p className="text-lg text-slate-600 max-w-2xl mb-9 leading-relaxed">
                            Licensed Fire Fighting, Sprinkler, Detection &
                            Suppression System experts — delivering turnkey fire safety
                            engineering for industrial, commercial & government projects
                            across Maharashtra.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                            <Link
                                to="/contact"
                                className="btn-primary"
                                data-testid="hero-cta-inspection"
                            >
                                Request Site Inspection
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/contact"
                                className="btn-secondary"
                                data-testid="hero-cta-quote"
                            >
                                Get Quotation
                            </Link>
                            <Link
                                to="/products"
                                className="btn-secondary"
                                data-testid="hero-cta-shop"
                            >
                                View Products
                            </Link>
                        </div>

                        {/* Trust indicators row */}
                        <div className="grid grid-cols-3 gap-5 mt-12 max-w-2xl">
                            {[
                                { k: "Licensed", v: "Fire Safety Agency", icon: ShieldCheck },
                                { k: "Turnkey", v: "Design to AMC", icon: Workflow },
                                { k: "24/7", v: "Site Support", icon: Headphones },
                            ].map((it) => (
                                <div
                                    key={it.k}
                                    className="border-l-2 border-[#E53935] pl-4"
                                >
                                    <it.icon className="w-4 h-4 text-[#E53935] mb-2" />
                                    <div className="text-xl md:text-2xl font-display font-bold text-slate-900 leading-none">
                                        {it.k}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono uppercase tracking-widest mt-1.5">
                                        {it.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right visual */}
                    <div className="lg:col-span-5 relative">
                        {/* Decorative offset shapes */}
                        <div
                            className="absolute -top-6 -left-6 w-28 h-28 border-2 border-[#E53935] hidden md:block"
                            style={{ zIndex: 0 }}
                        />
                        <div
                            className="absolute -bottom-6 -right-6 w-36 h-36 bg-[#E53935]/10 hidden md:block"
                            style={{ zIndex: 0 }}
                        />

                        {/* Main image card */}
                        <div
                            className="relative overflow-hidden corner-accent shadow-2xl"
                            style={{ aspectRatio: "1/1", zIndex: 1, background: "#fff1f0" }}
                        >
                            <img
                                src={HERO_IMAGE}
                                alt="VIL Engineering — full fire safety equipment lineup: hose reel, extinguishers, alarm panel, detectors"
                                className="w-full h-full object-cover object-center"
                            />
                            {/* Subtle red wash + bottom darken */}
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(229,57,53,0.08) 0%, transparent 35%, rgba(10,10,12,0.65) 100%)",
                                }}
                            />

                            {/* Top-left floating badge */}
                            <div className="absolute top-5 left-5 bg-white px-4 py-2.5 shadow-lg flex items-center gap-3">
                                <div className="w-8 h-8 bg-[#fff1f0] flex items-center justify-center">
                                    <ShieldCheck className="w-4 h-4 text-[#E53935]" />
                                </div>
                                <div>
                                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
                                        Licensed
                                    </div>
                                    <div className="text-xs font-display font-bold text-slate-900">
                                        Licensed Agency
                                    </div>
                                </div>
                            </div>

                            {/* Top-right system tag */}
                            <div
                                className="absolute top-5 right-5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white"
                                style={{ background: "rgba(229,57,53,0.95)" }}
                            >
                                Fire Hydrant System
                            </div>

                            {/* Emergency contact card */}
                            <div
                                className="absolute bottom-5 left-5 right-5 backdrop-blur-xl p-5"
                                style={{ background: "rgba(10,10,12,0.88)" }}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="eyebrow !text-[10px] !text-[#FF8A80] mb-1">
                                            Emergency · 24 × 7
                                        </div>
                                        <a
                                            href={`tel:${COMPANY.primaryPhoneRaw}`}
                                            className="font-display font-bold text-white text-xl hover:text-[#FF8A80] transition"
                                        >
                                            {COMPANY.primaryPhone}
                                        </a>
                                    </div>
                                    <a
                                        href={`tel:${COMPANY.primaryPhoneRaw}`}
                                        className="w-12 h-12 rounded-full bg-[#E53935] flex items-center justify-center text-white flex-shrink-0 hover:scale-110 transition"
                                        style={{
                                            boxShadow: "0 0 24px rgba(229,57,53,0.6)",
                                        }}
                                        aria-label="Call now"
                                    >
                                        <Phone className="w-5 h-5" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Floating stat card */}
                        <div
                            className="absolute bottom-[-30px] left-[-30px] bg-white p-4 shadow-xl border border-slate-200 hidden md:flex items-center gap-3"
                            style={{ zIndex: 5 }}
                            data-testid="hero-stat-card"
                        >
                            <div className="w-12 h-12 bg-[#fff1f0] flex items-center justify-center">
                                <Award className="w-5 h-5 text-[#E53935]" />
                            </div>
                            <div>
                                <div className="text-2xl font-display font-black text-slate-900 leading-none">
                                    <Counter to={250} suffix="" />
                                    <span className="text-[#E53935]">+</span>
                                </div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">
                                    Projects Delivered
                                </div>
                            </div>
                        </div>

                        {/* Floating mini card top-right offset */}
                        <div
                            className="absolute top-[15%] right-[-30px] bg-white p-3 shadow-xl border border-slate-200 hidden lg:flex items-center gap-2.5"
                            style={{ zIndex: 5 }}
                        >
                            <div className="w-9 h-9 bg-[#e8f1fc] border border-[#0E4DA4]/20 flex items-center justify-center">
                                <CheckCircle2 className="w-4 h-4 text-[#0E4DA4]" />
                            </div>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                                    Compliance
                                </div>
                                <div className="text-xs font-display font-bold text-slate-900">
                                    NBC + IS Certified
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const TrustMarquee = () => {
    const items = [...TRUST_BADGES, ...TRUST_BADGES];
    return (
        <section
            className="border-y border-slate-200 py-6 overflow-hidden bg-slate-50"
            data-testid="trust-marquee"
        >
            <div className="flex marquee-track w-max">
                {items.map((b, i) => (
                    <div key={i} className="flex items-center gap-3 px-10 text-slate-700">
                        <Icon name={b.icon} className={`w-5 h-5 ${i % 2 === 0 ? "text-[#E53935]" : "text-[#0E4DA4]"}`} />
                        <span className="text-sm font-mono uppercase tracking-widest whitespace-nowrap">
                            {b.label}
                        </span>
                        <span className="text-slate-300 ml-10">/</span>
                    </div>
                ))}
            </div>
        </section>
    );
};

const ServicesGrid = () => {
    return (
        <section className="section bg-white" data-testid="services-grid-section">
            <div className="container-vil">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="eyebrow">/ 01 — Our Capabilities</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
                            Engineered fire safety,<br />
                            <span className="text-[#E53935]">end-to-end.</span>
                        </h2>
                    </div>
                    <p className="text-slate-600 max-w-md leading-relaxed">
                        Licensed and government-approved installations across fire fighting,
                        detection, suppression, pump room & AMC services.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {SERVICES.map((s, i) => {
                        const isBlue = s.theme === "blue";
                        return (
                            <Link
                                key={s.slug}
                                to="/services"
                                className="group glass corner-accent relative p-7 card-lift overflow-hidden"
                                data-testid={`service-card-${s.slug}`}
                            >
                                <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-5 blur-3xl transition duration-700 ${isBlue ? "bg-[#0E4DA4]" : "bg-[#E53935]"}`} />
                                <div className="flex items-start justify-between mb-5">
                                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isBlue ? "bg-[#0E4DA4]" : "bg-[#E53935]"}`}>
                                        <Icon name={s.icon} className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-xs font-mono text-slate-300">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display font-bold text-slate-900 text-xl mb-2">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                                    {s.short}
                                </p>
                                <div className={`flex items-center gap-2 text-sm font-medium uppercase tracking-widest ${isBlue ? "text-[#0E4DA4]" : "text-[#E53935]"}`}>
                                    Explore
                                    <ArrowUpRight className="w-4 h-4 transition group-hover:translate-x-1 group-hover:-translate-y-1" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

const AboutStats = () => {
    return (
        <section
            className="section relative overflow-hidden bg-slate-50"
            data-testid="about-stats-section"
        >
            <div className="container-vil grid lg:grid-cols-2 gap-14 items-center">
                <div className="relative">
                    <div className="relative overflow-hidden corner-accent">
                        <img
                            src={ABOUT_IMAGE}
                            alt="Engineers reviewing fire safety blueprints"
                            className="w-full h-[520px] object-cover img-zoom group"
                        />
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    "linear-gradient(45deg, rgba(229,57,53,0.12) 0%, transparent 60%)",
                            }}
                        />
                    </div>
                    <div className="absolute -bottom-8 -right-4 md:right-8 glass corner-accent p-6 max-w-xs">
                        <div className="eyebrow mb-2">Industry Approval</div>
                        <p className="text-slate-900 font-display font-bold text-lg leading-tight">
                            Licensed by Maharashtra Fire Services
                        </p>
                    </div>
                </div>

                <div>
                    <span className="eyebrow">/ 02 — About VIL</span>
                    <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                        Two decades of fire safety <span className="text-[#E53935]">precision.</span>
                    </h2>
                    <p className="text-slate-600 mt-6 leading-relaxed">
                        VIL Engineering is a licensed fire safety contractor headquartered in
                        Palghar, Maharashtra. Our team designs, supplies, installs, and
                        maintains fire protection systems for industrial factories, MIDC
                        units, hospitals, schools, government buildings, and commercial
                        towers.
                    </p>
                    <p className="text-slate-600 mt-4 leading-relaxed">
                        We are authorized for execution of fire fighting, sprinkler,
                        detection and fire suppression systems — backed by strict adherence to
                        NBC, IS, and state fire compliance norms.
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-10 pt-10 border-t border-slate-200">
                        {STATS.slice(0, 3).map((s, i) => (
                            <div key={s.label} data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
                                <div className={`font-display font-black text-4xl md:text-5xl ${i % 2 === 1 ? "text-[#0E4DA4]" : "text-[#E53935]"}`}>
                                    <Counter to={s.value} suffix={s.suffix} />
                                </div>
                                <div className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-2">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-6">
                        {STATS.slice(3).map((s, i) => (
                            <div key={s.label} data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
                                <div className={`font-display font-black text-4xl md:text-5xl ${i % 2 === 0 ? "text-[#0E4DA4]" : "text-[#E53935]"}`}>
                                    <Counter to={s.value} suffix={s.suffix} />
                                </div>
                                <div className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-2">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    <Link to="/about" className="btn-secondary mt-10" data-testid="about-learn-more">
                        Learn About VIL <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
};

const WhyChooseUs = () => {
    return (
        <section className="section bg-white" data-testid="why-us-section">
            <div className="container-vil">
                <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
                    <div>
                        <span className="eyebrow">/ 03 — Why VIL</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight max-w-2xl">
                            Built on trust. Engineered for{" "}
                            <span className="text-[#E53935]">compliance.</span>
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {WHY_US.map((w, i) => {
                        const isBlue = w.theme === "blue";
                        return (
                            <div key={w.title} className="glass relative p-7 card-lift" data-testid={`why-card-${i}`}>
                                <div className="flex items-center gap-4 mb-4">
                                    <div className={`w-11 h-11 flex items-center justify-center ${isBlue ? "bg-[#0E4DA4]" : "bg-[#E53935]"}`}>
                                        <Icon name={w.icon} className="w-5 h-5 text-white" />
                                    </div>
                                    <span className="text-xs font-mono text-slate-300">
                                        /{String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display font-bold text-slate-900 text-lg mb-2">
                                    {w.title}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

const ProductsCarousel = () => {
    return (
        <section
            className="section relative overflow-hidden bg-slate-50"
            data-testid="products-carousel-section"
        >
            <div className="container-vil">
                <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-12">
                    <div>
                        <span className="eyebrow">/ 04 — Featured Products</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4">
                            Industrial-grade fire safety equipment
                        </h2>
                    </div>
                    <Link
                        to="/products"
                        className="btn-secondary self-start md:self-end"
                        data-testid="view-all-products"
                    >
                        View All Products <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                <Carousel
                    opts={{ align: "start", loop: true }}
                    className="w-full"
                    data-testid="products-carousel"
                >
                    <CarouselContent>
                        {PRODUCTS.map((p) => {
                            const waMsg = encodeURIComponent(
                                `Hello VIL, I'd like a quotation for: ${p.name}`
                            );
                            return (
                                <CarouselItem
                                    key={p.id}
                                    className="basis-full sm:basis-1/2 lg:basis-1/3"
                                >
                                    <div
                                        className="group glass overflow-hidden corner-accent relative h-full flex flex-col"
                                        data-testid={`product-carousel-${p.id}`}
                                    >
                                        <div className="overflow-hidden h-56 bg-slate-100">
                                            <img
                                                src={p.image}
                                                alt={p.name}
                                                className="w-full h-full object-cover img-zoom"
                                            />
                                        </div>
                                        <div className="p-5 flex-1 flex flex-col">
                                            <div className="text-xs font-mono text-[#E53935] uppercase tracking-widest mb-2">
                                                {p.category}
                                            </div>
                                            <h4 className="font-display font-bold text-slate-900 text-base mb-2 leading-snug min-h-[3rem]">
                                                {p.name}
                                            </h4>
                                            <p className="text-xs text-slate-500 mb-5 flex-1">
                                                {p.spec}
                                            </p>
                                            <a
                                                href={`https://wa.me/${COMPANY.whatsapp}?text=${waMsg}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn-primary justify-center !py-2.5 text-sm"
                                            >
                                                WhatsApp Enquiry
                                            </a>
                                        </div>
                                    </div>
                                </CarouselItem>
                            );
                        })}
                    </CarouselContent>
                    <CarouselPrevious className="hidden sm:flex -left-4 bg-white border-slate-200 text-slate-900 hover:bg-[#E53935] hover:text-white hover:border-[#E53935]" />
                    <CarouselNext className="hidden sm:flex -right-4 bg-white border-slate-200 text-slate-900 hover:bg-[#E53935] hover:text-white hover:border-[#E53935]" />
                </Carousel>
            </div>
        </section>
    );
};

const BlogInsights = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();

        const loadBlogs = async () => {
            try {
                const result = await fetchPublicBlogs(controller.signal);
                setBlogs(result.blogs.slice(0, 3));
            } catch (err) {
                if (err.name !== "AbortError") {
                    setBlogs([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadBlogs();

        return () => controller.abort();
    }, []);

    if (!loading && blogs.length === 0) {
        return null;
    }

    return (
        <section
            className="section relative overflow-hidden bg-white"
            data-testid="home-blogs-section"
        >
            <div className="absolute inset-0 pointer-events-none opacity-60">
                <div
                    className="absolute right-0 top-0 w-1/2 h-full"
                    style={{
                        background:
                            "linear-gradient(135deg, transparent 0%, rgba(14,77,164,0.08) 50%, rgba(229,57,53,0.08) 100%)",
                    }}
                />
                <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(90deg, #0E4DA4 1px, transparent 1px), linear-gradient(0deg, #E53935 1px, transparent 1px)",
                        backgroundSize: "42px 42px",
                    }}
                />
            </div>

            <div className="container-vil relative z-10">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 mb-4">
                            <Sparkles className="w-4 h-4 text-[#E53935]" />
                            <span className="eyebrow">/ 05 - Knowledge Center</span>
                        </div>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl leading-tight">
                            High-value fire safety insights for better decisions.
                        </h2>
                        <p className="text-slate-600 mt-5 leading-relaxed max-w-2xl">
                            Read practical guidance from VIL Engineering on compliance,
                            system selection, maintenance readiness, and safer site operations.
                        </p>
                    </div>

                    <Link
                        to="/blogs"
                        className="btn-secondary self-start lg:self-end"
                        data-testid="view-all-blogs"
                    >
                        View All Blogs <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-3 gap-5" data-testid="home-blogs-loading">
                        {[0, 1, 2].map((item) => (
                            <div key={item} className="glass p-6 h-80 animate-pulse">
                                <div className="h-36 bg-slate-100 mb-6" />
                                <div className="h-4 bg-slate-100 w-1/3 mb-4" />
                                <div className="h-6 bg-slate-100 w-4/5 mb-3" />
                                <div className="h-4 bg-slate-100 w-full" />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-[1.35fr_0.9fr] gap-5">
                        <BlogCard blog={blogs[0]} featured />
                        <div className="grid gap-5">
                            {blogs.slice(1).map((blog) => (
                                <BlogCard key={blog.id} blog={blog} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

const IndustriesGrid = () => {
    return (
        <section className="section bg-white" data-testid="industries-section">
            <div className="container-vil">
                <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
                    <div>
                        <span className="eyebrow">/ 06 - Industries Served</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                            From MIDC factories to <span className="text-[#0E4DA4]">civic landmarks.</span>
                        </h2>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {INDUSTRIES.map((it, i) => (
                        <div
                            key={it.name}
                            className="group relative h-56 overflow-hidden corner-accent"
                            data-testid={`industry-${i}`}
                        >
                            <img
                                src={it.image}
                                alt={it.name}
                                className="w-full h-full object-cover img-zoom"
                            />
                            <div
                                className="absolute inset-0 transition duration-500"
                                style={{
                                    background:
                                        "linear-gradient(0deg, rgba(10,10,12,0.92) 0%, rgba(10,10,12,0.3) 100%)",
                                }}
                            />
                            <div className="absolute inset-0 p-5 flex flex-col justify-end">
                                <div className="text-xs font-mono text-white uppercase tracking-widest mb-1">
                                    /{String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="font-display font-bold text-lg" style={{ color: 'white' }}>
                                    {it.name}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Client logos */}
                <div className="mt-20 pt-12 border-t border-slate-200">
                    <p className="eyebrow text-center mb-10">Trusted by</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                        {CLIENT_LOGOS.map((c) => (
                            <ClientLogo key={c.name} client={c} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const Testimonials = () => {
    const [idx, setIdx] = useState(0);
    const t = TESTIMONIALS[idx];
    return (
        <section
            className="section relative overflow-hidden bg-slate-50"
            data-testid="testimonials-section"
        >
            <div
                className="absolute left-1/4 top-1/4 w-[500px] h-[500px] pointer-events-none opacity-50"
                style={{
                    background:
                        "radial-gradient(circle, rgba(14,77,164,0.16) 0%, transparent 60%)",
                }}
            />
            <div className="red-glow right-1/4 bottom-0" />
            <div className="container-vil relative z-10">
                <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-6">
                    <div>
                        <span className="eyebrow">/ 07 - Client Voices</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4">
                            What our partners say
                        </h2>
                    </div>
                </div>

                <div className="glass corner-accent relative p-8 md:p-14 max-w-4xl mx-auto">
                    <Quote className="w-12 h-12 text-[#0E4DA4] mb-6" />
                    <p className="font-display text-xl md:text-2xl text-slate-900 leading-relaxed mb-8">
                        "{t.quote}"
                    </p>
                    <div className="flex justify-between items-end flex-wrap gap-4">
                        <div>
                            <div className="font-display font-bold text-slate-900">{t.author}</div>
                            <div className="text-sm text-slate-500 font-mono uppercase tracking-widest mt-1">
                                {t.company}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {TESTIMONIALS.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    className={`w-10 h-1 transition ${
                                        i === idx ? "bg-[#0E4DA4]" : "bg-slate-300"
                                    }`}
                                    data-testid={`testimonial-dot-${i}`}
                                    aria-label={`Testimonial ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ContactSection = () => {
    return (
        <section className="section bg-white" data-testid="home-contact-section">
            <div className="container-vil grid lg:grid-cols-2 gap-14 items-start">
                <div>
                    <span className="eyebrow">/ 08 - Request a Quote</span>
                    <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                        Schedule your <span className="text-[#E53935]">free</span><br />
                        site inspection.
                    </h2>
                    <p className="text-slate-600 mt-6 leading-relaxed max-w-md">
                        Tell us about your facility and requirement. Our technical team
                        will reach out within 24 hours with a tailored proposal.
                    </p>

                    <div className="mt-10 space-y-5">
                        <ContactRow
                            icon={<MapPin className="w-5 h-5 text-[#E53935]" />}
                            title="Visit our office"
                        >
                            <p className="text-slate-700">{COMPANY.address}</p>
                        </ContactRow>
                        <ContactRow
                            icon={<CheckCircle2 className="w-5 h-5 text-[#E53935]" />}
                            title="Licensed Fire Safety Agency"
                        >
                            <p className="text-slate-700">
                                Authorized for Fire Fighting, Sprinkler, Detection & Suppression
                            </p>
                        </ContactRow>
                    </div>
                </div>

                <LeadForm />
            </div>
        </section>
    );
};

const ContactRow = ({ icon, title, children }) => (
    <div className="flex gap-4">
        <div className="w-11 h-11 flex items-center justify-center bg-[#fff1f0] border border-[#E53935]/20 flex-shrink-0">
            {icon}
        </div>
        <div>
            <div className="eyebrow !text-slate-500 !text-[10px] mb-1">{title}</div>
            {children}
        </div>
    </div>
);

const ClientLogo = ({ client }) => {
    const [failed, setFailed] = React.useState(false);
    const logoSrc = client.logo ?? `https://logo.clearbit.com/${client.domain}?size=200`;
    return (
        <div
            className="h-20 flex items-center justify-center px-4 border border-slate-200 bg-white hover:border-[#E53935]/40 hover:shadow-md transition"
            data-testid={`client-logo-${client.name.replace(/\s/g, "-").toLowerCase()}`}
        >
            {!failed ? (
                <img
                    src={logoSrc}
                    alt={`${client.name} logo`}
                    className="max-h-10 max-w-full object-contain grayscale hover:grayscale-0 transition"
                    onError={() => setFailed(true)}
                    loading="lazy"
                />
            ) : (
                <span className="font-display font-black text-slate-600 text-sm tracking-tight text-center">
                    {client.name}
                </span>
            )}
        </div>
    );
};

export default Home;
